import { cycleDueDate } from '../../../supabase/functions/_shared/lifecycle'
import { AUTO_CONFIRM_HOURS } from '@/constants/app.constants'

/**
 * İki tarih arasındaki gün farkını hesaplar
 */
export function daysBetween(date1: Date, date2: Date): number {
  const oneDay = 24 * 60 * 60 * 1000
  const diffMs = date2.getTime() - date1.getTime()
  return Math.round(diffMs / oneDay)
}

/**
 * Belirtilen tarihin (saat/dakika dahil) geçmiş olup olmadığını kontrol eder.
 */
export function isPastDate(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.getTime() < new Date().getTime()
}

/**
 * İlk tarihin ikinci tarihten önce olup olmadığını kontrol eder.
 */
export function isBeforeDate(date1: Date | string, date2: Date | string): boolean {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2
  return d1.getTime() < d2.getTime()
}

/**
 * Ödemenin gecikip gecikmediğini kontrol eder
 */
export function checkPaymentStatus(
  dueDate: Date,
  timezone: string = 'Europe/Istanbul'
): { isOverdue: boolean; daysOverdue: number } {
  const now = new Date()
  const localNow = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
  const daysOverdue = daysBetween(dueDate, localNow)
  return {
    isOverdue: daysOverdue > 0,
    daysOverdue: Math.max(0, daysOverdue),
  }
}

/**
 * Gece saatlerini (22:00 - 09:00 TSI) dikkate alarak efektif geçen süreyi hesaplar
 */
export function calculateEffectiveHours(start: Date, end: Date): number {
  if (start >= end) return 0

  let current = new Date(start)
  let effectiveMs = 0

  while (current < end) {
    const hours = current.getUTCHours() + 3 // TSİ (UTC+3)
    const normalizedHours = hours >= 24 ? hours - 24 : hours

    // TSİ 22:00 (dahil) ile 09:00 (hariç) arası gece sayılır
    const isNight = normalizedHours >= 22 || normalizedHours < 9

    const nextHour = new Date(current)
    nextHour.setUTCMinutes(0, 0, 0)
    nextHour.setUTCHours(nextHour.getUTCHours() + 1)

    const stepEnd = nextHour < end ? nextHour : end

    if (!isNight) {
      effectiveMs += stepEnd.getTime() - current.getTime()
    }

    current = stepEnd
  }

  return effectiveMs / (1000 * 60 * 60)
}

/**
 * Beyan edilmiş ödemenin otomatik onaylanıp onaylanmayacağını kontrol eder
 */
export function shouldAutoConfirm(declaredAt: Date): boolean {
  const now = new Date()
  const effectiveHours = calculateEffectiveHours(declaredAt, now)
  return effectiveHours >= AUTO_CONFIRM_HOURS
}

/**
 * Tarihi kullanıcı dostu formatta gösterir
 */
export function formatDate(
  date: Date | string,
  locale: string = 'tr-TR',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat(locale, options).format(d)
}

/**
 * Göreceli zaman formatı (örn: "2 saat önce")
 */
export function formatRelativeTime(date: Date | string, locale: string = 'tr-TR'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (diffSeconds < 0) return 'az önce'
  if (diffDays > 0) return rtf.format(-diffDays, 'day')
  if (diffHours > 0) return rtf.format(-diffHours, 'hour')
  if (diffMinutes > 0) return rtf.format(-diffMinutes, 'minute')
  return 'az önce'
}

/**
 * Tarih ve Saati Formatlar (8 Ocak 2024, 14:30)
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * Tarihi ve saati ayrı ayrı, dateInfo ve hourInfo objesi olarak formatlar
 */
export function formatDateWithHour(
  isoString?: Date | string | null
): { dateInfo: string; hourInfo: string } | null {
  if (!isoString) return null
  try {
    const d = typeof isoString === 'string' ? new Date(isoString) : isoString
    return {
      dateInfo: d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
      hourInfo: d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    }
  } catch (_e) {
    return null
  }
}

/**
 * Sonraki ödeme tarihini hesaplar
 */
export function calculateNextPaymentDate(
  startDate: Date,
  cycleNumber: number,
  cyclePeriod: 'monthly' | 'weekly' = 'monthly'
): Date {
  // `cycleNumber` burada OFFSET'tir (dönem numarası - 1). Dokuz çağrı
  // yeri böyle yazılmış, imza korundu; motor 1 tabanlı bekliyor.
  const iso = gunIsoYaz(startDate)
  const sonuc = cycleDueDate(iso, cycleNumber + 1, cyclePeriod)
  // Motor geçersiz girdide boş dize döner; eski davranış her hâlükârda
  // bir Date döndürmekti, çağrı yerleri `isNaN` ile kontrol ediyor.
  if (!sonuc) return new Date(NaN)

  // GÜNÜ motor belirler, SAATİ girdiden korunur. Eski gövde tarihi
  // yerinde değiştirdiği için saat kısmı olduğu gibi kalıyordu ve çağrı
  // yerleri buna güveniyor.
  const [y, ay, gun] = sonuc.split('-').map(Number)
  const cikti = new Date(startDate)
  cikti.setFullYear(y, ay - 1, gun)
  return cikti
}

/** Date -> 'YYYY-MM-DD' (YEREL gün; çağrı yerleri yerel Date üretiyor). */
function gunIsoYaz(d: Date): string {
  if (isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const a = String(d.getMonth() + 1).padStart(2, '0')
  const g = String(d.getDate()).padStart(2, '0')
  return `${y}-${a}-${g}`
}
